import React, { FC, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import dayjs from 'dayjs';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Autocomplete,
	TextField,
	Box,
	Typography,
	CircularProgress,
	useTheme,
	useMediaQuery,
} from '@mui/material';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { useMobileDetect } from 'core/hooks/useMobileDetect';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
	getFullscreenContainer,
	getFullscreenPopperSlotProps,
} from 'core/utils/fullscreenContainer';
import {
	filterPositionsByJobCode,
	NONE_POSITION_OPTION,
	resolvePositionSelection,
} from '../../utils/scheduleDepartmentUtils';
import { getShiftLengthValidationError } from '../../utils/shiftValidation';
import {
	calculateEmployeeStatusForShift,
	type EmployeeStatusData,
	type ShiftLike,
} from '../../utils/scheduleEmployeeStatus';
import unavailableIcon from 'assets/images/unavailable.png';
import minorIcon from 'assets/images/minor.png';
import overtimeIcon from 'assets/images/overtime.png';

export type ShiftSchedulerDialogMode =
	| 'quick-edit-day'
	| 'quick-edit-week'
	| 'create-day'
	| 'create-week';

export interface ShiftCreateContext {
	startAt: Date;
	endAt: Date;
	jobCodeId?: string | number;
	employeeId?: string | number | null;
}

interface ShiftSchedulerDialogProps {
	open: boolean;
	mode: ShiftSchedulerDialogMode;
	shift?: any;
	createContext?: ShiftCreateContext;
	employees: any[];
	jobCodes: any[];
	positions?: any[];
	minShiftLength?: number;
	maxShiftLength?: number;
	generalSettings?: {
		ageOfMinor?: number;
		overtimeDailyHoursAt?: number;
		overtimeDailyWarningAt?: number;
		overtimeWeeklyHoursAt?: number;
		overtimeWeeklyWarningAt?: number;
	} | null;
	daysData?: Array<{ position?: string | number; label?: string; ['@id']?: string }>;
	weekShifts?: ShiftLike[];
	weekStartDate?: string | Date;
	weekEndDate?: string | Date;
	isSaving?: boolean;
	onClose: () => void;
	onSaveDayUser: (employeeId: string | null) => void;
	onSaveDayPosition: (position: string | null) => void;
	onSaveWeekFields: (fields: {
		jobCode: string;
		position: string | null;
		startAt: string;
		endAt: string;
	}) => void;
	onCreateDay: (employeeId: string | null, position?: string | null) => void;
	onCreateWeek: (fields: {
		jobCode: string;
		position: string | null;
		startAt: string;
		endAt: string;
	}) => void;
	onOpenFullEdit?: () => void;
}

const HOUSE_SHIFT_OPTION = { id: 'HOUSE_SHIFT', firstName: 'House', lastName: 'Shift', '@id': 'null' };

const ShiftSchedulerDialog: FC<ShiftSchedulerDialogProps> = ({
	open,
	mode,
	shift,
	createContext,
	employees,
	jobCodes,
	positions = [],
	minShiftLength,
	maxShiftLength,
	generalSettings,
	daysData,
	weekShifts = [],
	weekStartDate,
	weekEndDate,
	isSaving = false,
	onClose,
	onSaveDayUser,
	onSaveDayPosition,
	onSaveWeekFields,
	onCreateDay,
	onCreateWeek,
	onOpenFullEdit,
}) => {
	const isDayMode = mode === 'quick-edit-day' || mode === 'create-day';
	const isCreateMode = mode === 'create-day' || mode === 'create-week';
	const isQuickEdit = mode === 'quick-edit-day' || mode === 'quick-edit-week';

	const theme = useTheme();
	const isTabletViewport = useMediaQuery(theme.breakpoints.down('lg'));
	const isMobileDevice = useMobileDetect().isMobile();
	const disablePickerTyping = isMobileDevice || isTabletViewport;

	const renderPickerInput = (
		params: AutocompleteRenderInputParams,
		placeholder: string,
		options?: { required?: boolean }
	) => (
		<TextField
			{...params}
			placeholder={placeholder}
			size="small"
			required={options?.required}
			slotProps={{
				htmlInput: {
					...params.inputProps,
					...(disablePickerTyping
						? { readOnly: true, inputMode: 'none' as const }
						: {}),
				},
			}}
		/>
	);

	const sortedEmployees = useMemo(() => {
		return [...(employees || [])].sort((a, b) =>
			`${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
		);
	}, [employees]);

	const employeeOptions = useMemo(
		() => [HOUSE_SHIFT_OPTION, ...sortedEmployees],
		[sortedEmployees]
	);

	const sortedJobCodes = useMemo(() => {
		if (!jobCodes) return [];
		return [...jobCodes].sort((a, b) => (a.jobNumber ?? 999) - (b.jobNumber ?? 999));
	}, [jobCodes]);

	const getInitialEmployee = () => {
		if (isCreateMode) return HOUSE_SHIFT_OPTION;
		if (!shift?.employee) return HOUSE_SHIFT_OPTION;
		return (
			sortedEmployees.find(
				(e) => e.id === shift.employee.id || e['@id'] === shift.employee['@id']
			) || HOUSE_SHIFT_OPTION
		);
	};

	const getInitialJobCode = () => {
		if (createContext?.jobCodeId) {
			return (
				sortedJobCodes.find(
					(jc) => jc.id === createContext.jobCodeId || String(jc.id) === String(createContext.jobCodeId)
				) || null
			);
		}
		if (shift?.jobCode) {
			return (
				sortedJobCodes.find(
					(jc) => jc.id === shift.jobCode.id || jc['@id'] === shift.jobCode['@id']
				) || null
			);
		}
		return null;
	};

	const getInitialStartAt = () => {
		if (createContext?.startAt) return moment(createContext.startAt).toISOString();
		if (shift?.startAt || shift?.start) {
			return moment(shift.startAt || shift.start).toISOString();
		}
		return moment().toISOString();
	};

	const getInitialEndAt = () => {
		if (createContext?.endAt) return moment(createContext.endAt).toISOString();
		if (shift?.endAt || shift?.end) {
			return moment(shift.endAt || shift.end).toISOString();
		}
		return moment().add(8, 'hours').toISOString();
	};

	const [selectedEmployee, setSelectedEmployee] = useState<any>(getInitialEmployee());
	const [selectedJobCode, setSelectedJobCode] = useState<any>(getInitialJobCode());
	const [selectedPosition, setSelectedPosition] = useState<any>(NONE_POSITION_OPTION);
	const [startAt, setStartAt] = useState<string>(getInitialStartAt());
	const [endAt, setEndAt] = useState<string>(getInitialEndAt());
	const [portalContainer, setPortalContainer] = useState<HTMLElement | undefined>(undefined);

	const getEmployeeOptionStatus = (employee: any): EmployeeStatusData | null => {
		if (!employee || employee.id === 'HOUSE_SHIFT' || !weekStartDate || !weekEndDate) {
			return null;
		}

		const currentShiftId = shift?.shift_id || shift?.id || 'draft';
		const draftShift: ShiftLike = {
			id: currentShiftId,
			employee: { id: employee.id, '@id': employee['@id'] },
			startAt,
			endAt,
		};
		const otherShifts = weekShifts.filter(
			(weekShift) =>
				String(weekShift.id) !== String(currentShiftId) &&
				String((weekShift as { shift_id?: string | number }).shift_id) !== String(currentShiftId)
		);

		return calculateEmployeeStatusForShift({
			employeeId: employee.id,
			currentShift: draftShift,
			allShifts: [...otherShifts, draftShift],
			fromDate: weekStartDate,
			toDate: weekEndDate,
			generalSettings,
			workplaceUser: employee,
			allDays: daysData,
		});
	};

	const selectedEmployeeStatus = useMemo(
		() => getEmployeeOptionStatus(selectedEmployee),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[selectedEmployee, startAt, endAt, weekShifts, weekStartDate, weekEndDate, generalSettings, daysData]
	);

	const renderEmployeeStatusIcons = (status: EmployeeStatusData | null) => {
		if (!status) return null;

		const isUnavailable = status.hasAvailabilityIssue || status.hasTimeOff;
		const hasOvertime =
			status.hasOvertime ||
			status.isApproachingDailyOvertime ||
			status.isApproachingWeeklyOvertime ||
			status.isShiftCausingDailyApproach ||
			status.isShiftCausingWeeklyApproach;

		if (!isUnavailable && !status.isMinor && !hasOvertime) return null;

		return (
			<Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
				{isUnavailable && (
					<img
						src={unavailableIcon}
						alt="Unavailable"
						title={status.availabilityMessage || 'Employee is unavailable or has time off'}
						style={{ width: 18, height: 18, objectFit: 'contain' }}
					/>
				)}
				{status.isMinor && (
					<img
						src={minorIcon}
						alt="Minor"
						title="Employee is a minor"
						style={{ width: 18, height: 18, objectFit: 'contain' }}
					/>
				)}
				{hasOvertime && (
					<img
						src={overtimeIcon}
						alt="Overtime"
						title="Employee is in or approaching overtime"
						style={{ width: 18, height: 18, objectFit: 'contain' }}
					/>
				)}
			</Box>
		);
	};

	useEffect(() => {
		if (!open) return;

		const syncPortalContainer = () => {
			setPortalContainer(getFullscreenContainer());
		};

		syncPortalContainer();
		document.addEventListener('fullscreenchange', syncPortalContainer);
		return () => document.removeEventListener('fullscreenchange', syncPortalContainer);
	}, [open]);

	const getInitialPosition = () =>
		resolvePositionSelection(shift?.position, positions);

	const positionOptionsForJobCode = useMemo(() => {
		const jobCodeId = selectedJobCode?.id ?? createContext?.jobCodeId;
		if (!selectedJobCode && jobCodeId == null) return [NONE_POSITION_OPTION];
		return [
			NONE_POSITION_OPTION,
			...filterPositionsByJobCode(
				positions,
				selectedJobCode?.['@id'],
				jobCodeId
			),
		];
	}, [positions, selectedJobCode, createContext?.jobCodeId]);

	useEffect(() => {
		if (!open) return;
		setSelectedEmployee(getInitialEmployee());
		setSelectedJobCode(getInitialJobCode());
		setSelectedPosition(getInitialPosition());
		setStartAt(getInitialStartAt());
		setEndAt(getInitialEndAt());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, mode, shift, createContext, positions, jobCodes]);

	useEffect(() => {
		if (!selectedPosition || selectedPosition['@id'] === 'none') return;
		const stillValid = positionOptionsForJobCode.some(
			(option) => option['@id'] === selectedPosition['@id']
		);
		if (!stillValid) {
			setSelectedPosition(NONE_POSITION_OPTION);
		}
	}, [positionOptionsForJobCode, selectedPosition]);

	const dialogTitle = useMemo(() => {
		if (isCreateMode) return 'Create Shift';
		return 'Quick Edit';
	}, [isCreateMode]);

	const handleEmployeeChange = (_: unknown, value: any) => {
		setSelectedEmployee(value || HOUSE_SHIFT_OPTION);
		const employeeId = value?.['@id'] === 'null' ? null : value?.['@id'] || null;

		if (mode === 'quick-edit-day') {
			onSaveDayUser(employeeId);
		}
	};

	const handlePositionChange = (_: unknown, value: any) => {
		const nextPosition = value || NONE_POSITION_OPTION;
		setSelectedPosition(nextPosition);
		const positionIri =
			nextPosition?.['@id'] === 'none' ? null : nextPosition?.['@id'] || null;

		if (mode === 'quick-edit-day') {
			onSaveDayPosition(positionIri);
		}
	};

	const showPositionField =
		positions.length > 0 && (mode === 'quick-edit-day' || mode === 'quick-edit-week' || isCreateMode);

	const shiftLengthError = useMemo(
		() =>
			getShiftLengthValidationError(startAt, endAt, {
				minShiftLength,
				maxShiftLength,
			}),
		[startAt, endAt, minShiftLength, maxShiftLength]
	);

	const handleCreate = () => {
		if (shiftLengthError) return;

		if (mode === 'create-day') {
			const employeeId =
				selectedEmployee?.['@id'] === 'null' ? null : selectedEmployee?.['@id'] || null;
			onCreateDay(
				employeeId,
				selectedPosition?.['@id'] === 'none' ? null : selectedPosition?.['@id'] || null
			);
			return;
		}

		if (mode === 'create-week' && selectedJobCode) {
			onCreateWeek({
				jobCode: selectedJobCode['@id'],
				position:
					selectedPosition?.['@id'] === 'none' ? null : selectedPosition?.['@id'] || null,
				startAt,
				endAt,
			});
			return;
		}

		if (mode === 'quick-edit-week' && selectedJobCode) {
			onSaveWeekFields({
				jobCode: selectedJobCode['@id'],
				position:
					selectedPosition?.['@id'] === 'none' ? null : selectedPosition?.['@id'] || null,
				startAt,
				endAt,
			});
		}
	};

	const handleTimeChange = (field: 'startAt' | 'endAt', timeValue: dayjs.Dayjs | null) => {
		if (!timeValue) return;
		const currentIso = field === 'startAt' ? startAt : endAt;
		const currentDate = moment(currentIso).format('YYYY-MM-DD');
		const updated = moment(`${currentDate} ${timeValue.format('HH:mm')}:00`, 'YYYY-MM-DD HH:mm:ss');
		const iso = updated.toISOString();
		if (field === 'startAt') setStartAt(iso);
		else setEndAt(iso);
	};

	const canSubmitWeek =
		Boolean(selectedJobCode) && moment(endAt).isAfter(moment(startAt)) && !shiftLengthError;

	const popperSlotProps = getFullscreenPopperSlotProps(portalContainer);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			container={portalContainer}
			aria-labelledby="shift-scheduler-dialog-title"
		>
			<DialogTitle id="shift-scheduler-dialog-title">{dialogTitle}</DialogTitle>
			<DialogContent>
				{isDayMode && (
					<Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
						<Box>
							<Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
								Assigned User
							</Typography>
							<Autocomplete
								options={employeeOptions}
								getOptionLabel={(option) =>
									option.id === 'HOUSE_SHIFT'
										? 'House Shift'
										: `${option.firstName} ${option.lastName}`
								}
								value={selectedEmployee}
								onChange={handleEmployeeChange}
								isOptionEqualToValue={(option, value) => option['@id'] === value?.['@id']}
								disabled={isSaving}
								openOnFocus={disablePickerTyping}
								slotProps={popperSlotProps}
								renderOption={(props, option) => {
									const { key: _key, ...otherProps } = props;
									if (option.id === 'HOUSE_SHIFT') {
										return (
											<li key={option['@id'] || option.id} {...otherProps}>
												House Shift
											</li>
										);
									}

									const status = getEmployeeOptionStatus(option);
									const availabilityMessage =
										status?.availabilityMessage ||
										(status?.hasTimeOff ? 'Employee has time off' : null);

									return (
										<li key={option['@id'] || option.id} {...otherProps}>
											<Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
												<Box sx={{ flex: 1, minWidth: 0 }}>
													<Typography variant="body2">
														{option.firstName} {option.lastName}
													</Typography>
													{availabilityMessage && (
														<Typography
															variant="caption"
															sx={{ color: 'warning.main', display: 'block' }}
														>
															{availabilityMessage}
														</Typography>
													)}
												</Box>
												{renderEmployeeStatusIcons(status)}
											</Box>
										</li>
									);
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										placeholder="Select employee"
										size="small"
										InputProps={{
											...params.InputProps,
											endAdornment: (
												<>
													{renderEmployeeStatusIcons(selectedEmployeeStatus)}
													{params.InputProps.endAdornment}
												</>
											),
										}}
										inputProps={{
											...params.inputProps,
											...(disablePickerTyping
												? { readOnly: true, inputMode: 'none' as const }
												: {}),
										}}
									/>
								)}
							/>
						</Box>
						{shiftLengthError && (
							<Typography variant="caption" color="error">
								{shiftLengthError}
							</Typography>
						)}
						{showPositionField && (
							<Box>
								<Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
									Position
								</Typography>
								<Autocomplete
									options={positionOptionsForJobCode}
									getOptionLabel={(option) => option.name}
									value={selectedPosition}
									onChange={handlePositionChange}
									isOptionEqualToValue={(option, value) => option['@id'] === value?.['@id']}
									disabled={isSaving || (!selectedJobCode && createContext?.jobCodeId == null)}
									openOnFocus={disablePickerTyping}
									slotProps={popperSlotProps}
									renderInput={(params) => renderPickerInput(params, 'Select position')}
								/>
							</Box>
						)}
					</Box>
				)}

				{!isDayMode && (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'flex-start',
								gap: 1.5,
								flexWrap: 'nowrap',
								width: '100%',
							}}
						>
							<Box
								sx={{
									flex: showPositionField ? '0 0 46%' : '1 1 auto',
									minWidth: 0,
								}}
							>
								<Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
									Job Code
								</Typography>
								<Autocomplete
									options={sortedJobCodes}
									getOptionLabel={(option) =>
										option.jobNumber ? `${option.jobNumber} - ${option.label}` : option.label
									}
									value={selectedJobCode}
									onChange={(_, value) => {
										setSelectedJobCode(value);
										setSelectedPosition(NONE_POSITION_OPTION);
									}}
									isOptionEqualToValue={(option, value) => option['@id'] === value?.['@id']}
									disabled={isSaving}
									openOnFocus={disablePickerTyping}
									slotProps={popperSlotProps}
									renderInput={(params) =>
										renderPickerInput(params, 'Select job code', { required: true })
									}
								/>
							</Box>
							{showPositionField && (
								<Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
									<Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
										Position
									</Typography>
									<Autocomplete
										options={positionOptionsForJobCode}
										getOptionLabel={(option) => option.name}
										value={selectedPosition}
										onChange={handlePositionChange}
										isOptionEqualToValue={(option, value) => option['@id'] === value?.['@id']}
										disabled={isSaving || !selectedJobCode}
										openOnFocus={disablePickerTyping}
										slotProps={popperSlotProps}
										renderInput={(params) => renderPickerInput(params, 'Select position')}
									/>
								</Box>
							)}
						</Box>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<Box sx={{ display: 'flex', gap: 2 }}>
								<Box sx={{ flex: 1 }}>
									<Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
										Start Time
									</Typography>
									<TimePicker
										value={dayjs(startAt).isValid() ? dayjs(startAt) : null}
										onChange={(value) => handleTimeChange('startAt', value)}
										disabled={isSaving}
										slotProps={{
											textField: {
												size: 'small',
												fullWidth: true,
												error: Boolean(shiftLengthError),
											},
											...popperSlotProps,
										}}
									/>
								</Box>
								<Box sx={{ flex: 1 }}>
									<Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
										End Time
									</Typography>
									<TimePicker
										value={dayjs(endAt).isValid() ? dayjs(endAt) : null}
										onChange={(value) => handleTimeChange('endAt', value)}
										disabled={isSaving}
										slotProps={{
											textField: {
												size: 'small',
												fullWidth: true,
												error: Boolean(shiftLengthError),
											},
											...popperSlotProps,
										}}
									/>
								</Box>
							</Box>
							{shiftLengthError && (
								<Typography variant="caption" color="error" sx={{ mt: -1 }}>
									{shiftLengthError}
								</Typography>
							)}
						</LocalizationProvider>
					</Box>
				)}
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
				<Box>
					{isQuickEdit && onOpenFullEdit && (
						<Button onClick={onOpenFullEdit} disabled={isSaving} sx={{ textTransform: 'none' }}>
							Open Full Edit
						</Button>
					)}
				</Box>
				<Box sx={{ display: 'flex', gap: 1 }}>
					<Button onClick={onClose} disabled={isSaving} sx={{ textTransform: 'none' }}>
						{mode === 'quick-edit-day' ? 'Close' : 'Cancel'}
					</Button>
					{(!isQuickEdit || !isDayMode) && (
						<Button
							variant="contained"
							onClick={handleCreate}
							disabled={isSaving || (isDayMode ? Boolean(shiftLengthError) : !canSubmitWeek)}
							sx={{
								textTransform: 'none',
								backgroundColor: 'rgb(68, 99, 136)',
								'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
							}}
						>
							{isSaving ? <CircularProgress size={18} color="inherit" /> : isCreateMode ? 'Create' : 'Save'}
						</Button>
					)}
				</Box>
			</DialogActions>
		</Dialog>
	);
};

export default ShiftSchedulerDialog;
